// Background queue for feedback submissions that failed on the network path.
// User sees the "Thanks" notice immediately; this drains in the background so
// the kiosk is never blocked on flaky LTE / Wi-Fi.
import axios from 'axios';

const KEY = 'myhub.feedback.queue.v1';
const DRAIN_INTERVAL_MS = 30 * 1000;
// Backend codes that won't ever succeed on retry — drop instead of looping.
// Policy: be conservative. INVALID_REFID / ID_MISMATCH may be temporary
// upstream-sync issues (Octacle eventual consistency), so we keep retrying
// those. Only true client-side bad-data errors are dropped.
const PERMANENT_CODES = new Set([
  'MISSING_REFID',
  'MISSING_FIELD',
  'INVALID_FIELD',
]);
// Hard ceiling on retries for any single entry. Without this, a poison-pill
// payload (server returns ok:false with a code we don't know is permanent,
// or rejects with a network error every time) would retry forever, growing
// the queue until localStorage hits its ~5MB cap and silently starts
// dropping NEW writes. 50 attempts at 30s each ≈ 25 minutes of retrying;
// anything still failing after that is almost certainly broken.
const MAX_ATTEMPTS = 50;
// The server rejects feedback whose timestamp is older than 24h
// (FEEDBACK_MAX_PAST_MS). An item that has waited longer than that is
// undeliverable, so drop it rather than carry it — bounds storage on a long
// outage so the queue can't grow unbounded toward the ~5MB localStorage cap.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function tooOld(entry) {
  const t = Date.parse(entry?.payload?.timestamp || entry?.enqueuedAt || '');
  return Number.isFinite(t) && (Date.now() - t) > MAX_AGE_MS;
}

let started = false;
let draining = false;
// Optional per-drain payload transform the owning app supplies via start().
// Used to re-attach short-lived, deliberately un-stored fields at send time —
// e.g. a FRESH ThingsBoard token for feedback incident classification: we strip
// the token at enqueue (it would be stale and bloat storage on a long outage)
// and mint a current one here, when we're back online and about to POST.
let enrich = null;

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function write(list) {
  try {
    localStorage.setItem(KEY, JSON.stringify(list));
  } catch (e) {
    console.warn('[submissionQueue] localStorage write failed', e?.message);
  }
}

// Common offline queue for ANY submission type. `endpoint` is the API path the
// item is POSTed to on drain (e.g. '/submitFeedback', '/api/service-request',
// '/api/cleaning-log'), stored per item so one queue serves every kiosk flow.
// Back-compat: enqueue(payload) (single arg) defaults to the feedback endpoint.
export function enqueue(endpoint, payload) {
  if (payload === undefined) { payload = endpoint; endpoint = '/submitFeedback'; }
  const list = read();
  list.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    endpoint,
    payload,
    enqueuedAt: new Date().toISOString(),
    attempts: 0,
  });
  write(list);
  console.log('[submissionQueue] enqueued', endpoint, '; depth=', list.length);
}

export function size() {
  return read().length;
}

// Decide what to do with an entry that did not succeed this round. Returns
// the entry to keep (with attempts++), or null if we should drop it (poison
// pill — too many retries, would otherwise loop forever and exhaust
// localStorage). Centralized so transient-rejection and network-error paths
// stay in lockstep.
function bumpOrDrop(entry, reason) {
  const nextAttempts = (entry.attempts || 0) + 1;
  if (nextAttempts >= MAX_ATTEMPTS) {
    console.error(
      '[submissionQueue] dropping after MAX_ATTEMPTS',
      entry.id, 'reason=', reason, 'attempts=', nextAttempts,
    );
    return null;
  }
  return { ...entry, attempts: nextAttempts };
}

export async function drain(apiBase) {
  if (draining) return;
  draining = true;
  try {
    const list = read();
    if (list.length === 0) return;

    const remaining = [];
    for (const entry of list) {
      if (tooOld(entry)) {
        console.warn('[submissionQueue] dropping stale (>24h, undeliverable)', entry.id);
        continue;
      }
      const endpoint = entry.endpoint || '/submitFeedback'; // back-compat
      try {
        // Re-hydrate send-time-only fields (e.g. a fresh tbToken) if the owning
        // app supplied an enricher. Never let a bad enricher lose the item.
        let payload = entry.payload;
        if (enrich) {
          try {
            const out = await enrich(entry);
            if (out) payload = out;
          } catch (e) {
            console.warn('[submissionQueue] enrich failed; sending stored payload', e?.message);
          }
        }
        const resp = await axios.post(`${apiBase}${endpoint}`, payload, {
          timeout: 8000,
        });
        // Two success conventions: feedback answers 200 with a body { ok, code }
        // (ok:false even on a handled rejection); service-request/cleaning-log
        // signal success only via HTTP 2xx (axios throws on non-2xx, so reaching
        // here = 2xx). So: an explicit body ok:false is a handled rejection
        // (drop if permanent, else retry); anything else that returned 2xx is
        // delivered.
        const data = resp?.data || {};
        if (data.ok === false) {
          if (PERMANENT_CODES.has(data.code)) {
            console.warn('[submissionQueue] dropping permanently-rejected', entry.id, endpoint, data.code);
            continue;
          }
          const kept = bumpOrDrop(entry, `transient-${data.code || 'unknown'}`);
          if (kept) remaining.push(kept);
          continue;
        }
        console.log('[submissionQueue] drained', entry.id, endpoint);
      } catch (err) {
        // Network / 5xx / timeout — keep and retry (or drop if maxed).
        const kept = bumpOrDrop(entry, `network-${err?.message || 'unknown'}`);
        if (kept) remaining.push(kept);
      }
    }

    // CRITICAL: the queue may have grown DURING the loop above — handleSubmit
    // can call enqueue() on a fresh failed POST while we were busy POSTing
    // older entries. If we naively did `write(remaining)` we would clobber
    // those new entries (lost-write race). Instead: re-read the queue at
    // write-time, identify entries we did NOT process this round (by id),
    // and merge them with `remaining`. This preserves the invariant "every
    // enqueued entry is either delivered or still in the queue."
    const processedIds = new Set(list.map((e) => e.id));
    const currentList = read();
    const newEntries = currentList.filter((e) => !processedIds.has(e.id));
    write([...remaining, ...newEntries]);
  } finally {
    draining = false;
  }
}

// Kicks off periodic + event-driven draining for the lifetime of the app.
// Idempotent — calling more than once is a no-op.
export function start(apiBase, opts = {}) {
  // Accept/refresh the enricher even if already started, so the owning app can
  // (re)bind its token source without caring about call order.
  if (typeof opts.enrich === 'function') enrich = opts.enrich;
  if (started) return;
  started = true;

  const tick = () => drain(apiBase).catch(() => {});

  // Drain on launch (handles items left over from a previous session).
  tick();

  setInterval(tick, DRAIN_INTERVAL_MS);

  if (typeof window !== 'undefined') {
    window.addEventListener('online', tick);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) tick();
    });
  }
}
